/* global $ */

import debounce from '../helpers/debounce';
import formatPrice from '../helpers/formatPrice';

const searchBox = {
  /**
   * Create searchbox
   * @param  {HTMLDivElement} container
   * @param  {Object} props
   * @return {void}
   */
  create(container, props) {
    this.props = props;
    this.container = container;
    this.sorting = this.container.querySelector('.js-search-box-sorting');
    this.sortingOptions = this.container.querySelectorAll('.js-search-box-sorting-item');
    this.content = this.container.querySelector('.js-search-box-content');
    this.input = this.container.querySelector('.js-search-box-input');
    this.type = this.container.querySelector('.js-search-box-type').value;
    this.selected = typeof props.searchboxSelected !== 'undefined' ?
      props.searchboxSelected.split(',').map(selected => parseInt(selected, 10)) :
      [];

    if (typeof props.searchboxApi !== 'undefined') {
      this.loadList(props.searchboxApi)
        .then(() => { this.outputList(this.list); });
    } else {
      this.preselect();
      this.indexList();
    }

    this.createListeners();
  },

  /**
   * Create event listeners
   * @return {void}
   */
  createListeners() {
    this.sort();

    const searchDebounced = debounce((search) => {
      this.searchList(search.length > 2 ? search : '');
    }, 200);

    this.input.addEventListener('keyup', (e) => {
      const search = e.currentTarget.value;
      searchDebounced(search);
    });

    this.container.querySelector('.js-search-box-all').addEventListener('click', () => {
      this.select(true);
    });

    this.container.querySelector('.js-search-box-none').addEventListener('click', () => {
      this.select(false);
    });
  },

  /**
   * Load the list from the API
   * @param  {string} api
   * @param  {string} sort
   * @param  {string} order
   * @return {Promise}
   */
  loadList(api, sort, order) {
    return window.axios({
      url: api,
      params: {
        sort,
        order,
      },
    })
      .then((res) => {
        this.list = res.data;
      });
  },

  /**
   * Search through the list
   * @param  {string} search
   * @return {void}
   */
  searchList(search) {
    const items = this.getAllItems();

    const normalizedSearch = this.normalizeSearch(search);

    items.forEach((item) => {
      if (search === '') {
        item.style.display = 'block';
        return;
      }

      const inTitle = this.normalizeSearch(item.dataset.title).indexOf(normalizedSearch) >= 0;
      const inSku = this.normalizeSearch(item.dataset.sku).indexOf(normalizedSearch) >= 0;

      item.style.display = inTitle || inSku ? 'flex' : 'none';
    });
  },

  /**
   * Output list
   * @param  {array} list
   * @return {void}
   */
  outputList(list) {
    this.content.innerHTML = this.renderList(list);
    this.indexList();
  },

  indexList() {
    this.getAllItems().forEach((node) => {
      const skuElement = node.querySelector('.js-search-box-sku');
      node.dataset.title = this.normalizeSearch(node.querySelector('label').innerHTML);
      node.dataset.sku = this.normalizeSearch(skuElement ? skuElement.value : '');
    });
  },

  /**
   * Retrieve all available items in the search box
   * @return {array}
   */
  getAllItems() {
    return Array.prototype.slice.call(this.content.childNodes).filter(node => node.nodeName === 'DIV');
  },

  /**
   * Normalize the search string to make it case-insensitive etc.
   * @param  {string} search
   * @return {string}
   */
  normalizeSearch(search) {
    return search.toLowerCase().trim().replace(/[\s-]/g, '');
  },

  /**
   * Render the list
   * @param  {array} data
   * @return {string}
   */
  renderList(data) {
    const name = this.props.searchboxName;

    let template = this.container.querySelector('.js-search-box-item-template').innerHTML;
    template = template.replace('#id#', this.id);
    template = template.replace('#name#', name);
    template = template.replace('#type#', this.type);

    return data.map((item) => {
      let itemTemplate = template;

      itemTemplate = itemTemplate.replace('#checked#', this.selected.indexOf(item.id) >= 0 ? 'checked' : '');

      Object.keys(item).forEach((key) => {
        let label = item[key];

        if (key === 'price') {
          label = formatPrice(item[key]);
        }

        itemTemplate = itemTemplate.replace(`#${key}#`, label);
      });

      return itemTemplate;
    }).join('');
  },

  preselect() {
    this.getAllItems().forEach((item) => {
      const checkbox = item.querySelector('input[type="checkbox"]');

      if (this.selected.indexOf(parseInt(checkbox.value, 10)) >= 0) {
        checkbox.checked = true;
      }
    });
  },

  /**
   * Handle checkbox check/uncheck
   * @param  {boolean} state
   * @return {void}
   */
  select(state) {
    const visibleItems = this.getAllItems().filter(item => !item.style.display || item.style.display === 'block' || item.style.display === 'flex');

    visibleItems.forEach((item) => {
      item.querySelector(`input[type="${this.type}"]`).checked = state;
    });
  },

  sort() {
    const $outputList = $('.searchable-scrollbox');

    $outputList.on('click', '.js-search-box-sorting-item', (e) => {
      let newOrder;
      const currentOrder = e.currentTarget.getAttribute('data-order');

      if (currentOrder === '') {
        newOrder = 'ASC';
      } else if (currentOrder === 'ASC') {
        newOrder = 'DESC';
      } else {
        newOrder = '';
      }

      e.currentTarget.setAttribute('data-order', newOrder);

      this.resetSorting(e.currentTarget);

      if (e.currentTarget.getAttribute('data-column') === 'checkbox') {
        this.sortCheckedBoxes(newOrder);

        return;
      }

      this.loadList(this.props.searchboxApi, e.currentTarget.getAttribute('data-column'), newOrder)
        .then(() => { this.outputList(this.list); });
    });
  },

  resetSorting(target) {
    const options = Array.prototype.slice.call(this.sortingOptions);

    options
      .filter(option => option !== target)
      .forEach((option) => {
        option.setAttribute('data-order', '');
      });
  },

  sortCheckedBoxes(order) {
    this.getAllItems().forEach((item) => {
      const isChecked = !!item.querySelector('input:checked');

      if (order === 'ASC') {
        item.style.display = isChecked ? 'flex' : 'none';
        return;
      }

      item.style.display = 'flex';
    });
  },
};

export default searchBox;
